import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  User,
  Globe,
  Loader2,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import type { AssetWithPermission } from '@/services/asset-service';
import { assetService } from '@/services/asset-service';
import { getRoleLabel, getRoleBadgeColor } from '@/lib/asset-permission-utils';

interface AssetShareModalProps {
  asset: AssetWithPermission;
  isOpen: boolean;
  onClose: () => void;
  onShareComplete?: () => void;
}

type SearchResult = {
  id: string;
  name: string;
  email?: string;
  slug?: string;
};

const AssetShareModal: React.FC<AssetShareModalProps> = ({
  asset,
  isOpen,
  onClose,
  onShareComplete
}) => {
  const [targetType, setTargetType] = useState<'user' | 'organization' | 'public'>('user');
  const [targetId, setTargetId] = useState('');
  const [targetName, setTargetName] = useState('');
  const [role, setRole] = useState<'owner' | 'editor' | 'viewer'>('viewer');
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Only owners can share
  if (!asset.permission || asset.permission.role !== 'owner') {
    return null;
  }

  // Search for users or organizations when query changes
  useEffect(() => {
    const performSearch = async () => {
      if (!searchQuery || searchQuery.length < 2 || targetType === 'public') {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        if (targetType === 'user') {
          const result = await assetService.searchUsers(searchQuery);
          setSearchResults(result.users.map(u => ({
            id: u.id,
            name: u.name,
            email: u.email,
          })));
        } else if (targetType === 'organization') {
          const result = await assetService.searchOrganizations(searchQuery);
          setSearchResults(result.organizations.map(o => ({
            id: o.id,
            name: o.name,
            slug: o.slug,
          })));
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(performSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, targetType]);

  // Reset search when target type changes
  useEffect(() => {
    setSearchQuery('');
    setSearchResults([]);
    setTargetId('');
    setTargetName('');
  }, [targetType]);

  const handleSelectSearchResult = (result: SearchResult) => {
    setTargetId(result.id);
    setTargetName(result.name);
    setSearchQuery(result.name);
    setSearchResults([]);
  };

  const handleShare = async () => {
    if (!targetId && targetType !== 'public') {
      toast.error('Please select a target to share with');
      return;
    }
    
    setIsLoading(true);
    try {
      await assetService.shareAsset(
        asset.uuid, 
        targetType, 
        targetType === 'public' ? '__PUBLIC__' : targetId, 
        role
      );
      toast.success(`Asset shared successfully as ${getRoleLabel(role)}`);
      onShareComplete?.();
      handleClose();
    } catch (error) {
      toast.error('Failed to share asset: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setTargetType('user');
    setTargetId('');
    setTargetName('');
    setRole('viewer');
    setIsLoading(false);
    onClose();
  };

  const getTargetIcon = (type: string) => {
    switch (type) {
      case 'user': return <User className="h-4 w-4" />;
      case 'organization': return <Users className="h-4 w-4" />;
      case 'public': return <Globe className="h-4 w-4" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share Asset: {asset.name}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="targetType">Share With</Label>
            <Select value={targetType} onValueChange={(v: any) => setTargetType(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select share type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Specific User</SelectItem>
                <SelectItem value="organization">Organization</SelectItem>
                <SelectItem value="public">Public Link</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Target</Label>
            <div className="space-y-2">
              {targetType === 'public' ? (
                <div className="flex items-center gap-2 p-3 rounded-md border">
                  {getTargetIcon('public')}
                  <span>Anyone with the link can access</span>
                  <Badge className="ml-auto" variant="outline">
                    {getRoleLabel(role)}
                  </Badge>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      placeholder={`Search for ${targetType === 'user' ? 'users' : 'organizations'}...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    )}

                    {/* Search results dropdown */}
                    {searchResults.length > 0 && !targetId && (
                      <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {searchResults.map((result) => (
                          <button
                            key={result.id}
                            type="button"
                            onClick={() => handleSelectSearchResult(result)}
                            className="w-full flex items-center gap-2 p-3 hover:bg-muted transition-colors text-left"
                          >
                            {getTargetIcon(targetType)}
                            <div className="flex-1">
                              <div className="font-medium">{result.name}</div>
                              {result.email && (
                                <div className="text-xs text-muted-foreground">{result.email}</div>
                              )}
                              {result.slug && (
                                <div className="text-xs text-muted-foreground">@{result.slug}</div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selected target display */}
                  {targetId && (
                    <div className="flex items-center gap-2 p-3 rounded-md border bg-muted/50">
                      {getTargetIcon(targetType)}
                      <div className="flex-1">
                        <div className="font-medium">{targetName || targetId}</div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setTargetId('');
                          setTargetName('');
                          setSearchQuery('');
                        }}
                      >
                        Change
                      </Button>
                    </div>
                  )}

                  {searchQuery && !isSearching && searchResults.length === 0 && searchQuery.length >= 2 && !targetId && (
                    <p className="text-sm text-muted-foreground italic">
                      No {targetType === 'user' ? 'users' : 'organizations'} found
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Permission Level</Label>
            <Select value={role} onValueChange={(v: any) => setRole(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select permission level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer (Read-only)</SelectItem>
                <SelectItem value="editor">Editor (Read & Edit)</SelectItem>
                <SelectItem value="owner">Owner (Full access)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="mt-4 p-3 bg-muted rounded-md text-sm">
            <p className="font-medium">What this means:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><span className="font-medium">Viewer:</span> Can view the asset only</li>
              <li><span className="font-medium">Editor:</span> Can view and edit the asset</li>
              <li><span className="font-medium">Owner:</span> Full access including sharing rights</li>
            </ul>
          </div>
        </div>
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            type="button" 
            onClick={handleShare}
            disabled={isLoading || (!targetId && targetType !== 'public')}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sharing...
              </>
            ) : (
              `Share Asset`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { AssetShareModal };